import { useEffect, useMemo, useState, type ReactNode } from "react";
import { IconeVoltar } from "../icones";
import { AlternarTema } from "../tema";

export type Marca = {
  nome: string;
  logoUrl?: string;
  /** Logo pra fundo claro (modo dia). Sem ele, o mesmo logo nos dois modos. */
  logoClaroUrl?: string;
  linkPlanos?: string;
  whatsapp?: string;
  suporteEmail?: string;
};

/** Gerador deterministico (a mesma marca desenha sempre o mesmo grafico). */
function aleatorio(semente: string) {
  let h = 1779033703 ^ semente.length;
  for (let i = 0; i < semente.length; i++) h = Math.imul(h ^ semente.charCodeAt(i), 3432918353), (h = (h << 13) | (h >>> 19));
  return () => {
    h = Math.imul(h ^ (h >>> 16), 2246822507);
    h = Math.imul(h ^ (h >>> 13), 3266489909);
    return ((h ^= h >>> 16) >>> 0) / 4294967296;
  };
}

/** Atmosfera de mesa de operacao: linha de preco com area, velas ao fundo. Puramente decorativo. */
export function GraficoAmbiente({ semente }: { semente: string }) {
  const { linha, area, velas, fim } = useMemo(() => {
    const r = aleatorio(semente);
    const pontos: [number, number][] = [];
    let y = 300;
    for (let i = 0; i <= 64; i++) {
      y += (r() - 0.56) * 26;
      y = Math.max(70, Math.min(360, y));
      pontos.push([(i / 64) * 1000, y]);
    }
    const linha = pontos.map(([x, yy], i) => `${i ? "L" : "M"}${x.toFixed(1)} ${yy.toFixed(1)}`).join(" ");
    const area = `${linha} L1000 400 L0 400 Z`;
    const velas = Array.from({ length: 42 }, (_, i) => {
      const x = 12 + i * 24;
      const base = pontos[Math.min(64, Math.round((x / 1000) * 64))]![1];
      const a = base + (r() - 0.5) * 50;
      const f = base + (r() - 0.5) * 50;
      return { x, topo: Math.min(a, f), altura: Math.max(3, Math.abs(a - f)), maxima: Math.min(a, f) - r() * 22, minima: Math.max(a, f) + r() * 22, alta: f < a };
    });
    return { linha, area, velas, fim: pontos[pontos.length - 1]! };
  }, [semente]);
  return (
    <svg className="mtc-grafico" viewBox="0 0 1000 400" preserveAspectRatio="none" aria-hidden>
      <defs>
        <linearGradient id="mtc-grafico-degrade" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="var(--acento)" stopOpacity="0.28" />
          <stop offset="100%" stopColor="var(--acento)" stopOpacity="0" />
        </linearGradient>
      </defs>
      {velas.map((v) => (
        <g key={v.x} className={`mtc-grafico__vela${v.alta ? " mtc-grafico__vela--alta" : ""}`}>
          <rect x={v.x - 0.6} y={v.maxima} width={1.2} height={v.minima - v.maxima} />
          <rect x={v.x - 5} y={v.topo} width={10} height={v.altura} />
        </g>
      ))}
      <path className="mtc-grafico__area" d={area} />
      <path className="mtc-grafico__linha" d={linha} vectorEffect="non-scaling-stroke" />
      <circle className="mtc-grafico__ponto" cx={fim[0] - 2} cy={fim[1]} r={5} />
    </svg>
  );
}

/** Hora de Brasilia, como no pregao. Atualiza a cada 15 s. */
export function Relogio() {
  const [agora, setAgora] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setAgora(new Date()), 15_000);
    return () => clearInterval(t);
  }, []);
  const partes = Object.fromEntries(new Intl.DateTimeFormat("pt-BR", { timeZone: "America/Sao_Paulo", weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }).formatToParts(agora).map((p) => [p.type, p.value]));
  const texto = `${(partes.weekday ?? "").replace(".", "")} ${partes.day} ${(partes.month ?? "").replace(".", "")}  ${partes.hour}:${partes.minute} BRT`;
  return (
    <span className="mtc-relogio"><i aria-hidden />{texto}</span>
  );
}

export function MarcaVisual({ marca, classe }: { marca: Marca; classe: string }) {
  if (!marca.logoUrl) return <span className="mtc-palco__marca-texto">{marca.nome}</span>;
  if (!marca.logoClaroUrl) return <img className={classe} src={marca.logoUrl} alt={marca.nome} />;
  // Dia e noite: as duas versoes do logo; o CSS mostra a do modo que esta na tela (estilo.css, "logo por modo").
  return (
    <>
      <img className={`${classe} mtc-logo--escuro`.trim()} src={marca.logoUrl} alt={marca.nome} />
      <img className={`${classe} mtc-logo--claro`.trim()} src={marca.logoClaroUrl} alt={marca.nome} />
    </>
  );
}

/**
 * Tela de acesso: palco da marca a esquerda (no celular, faixa no topo), formulario a direita.
 * `rotulo` diz onde a pessoa esta ("Area do cliente", "Painel de operacao"). `voltarHref`: o "Voltar" no topo do
 * formulario pra um endereco de fora (a pagina de cobranca volta pro site da marca, spec 045, 3.7).
 */
export function TelaDeAcesso({ marca, rotulo, frase, children, rodape, voltarHref }: { marca: Marca; rotulo: string; frase?: string; children: ReactNode; rodape?: ReactNode; voltarHref?: string }) {
  return (
    <div className="mtc-acesso">
      <aside className="mtc-palco" aria-label={marca.nome}>
        <div className="mtc-palco__topo">
          <MarcaVisual marca={marca} classe="mtc-palco__logo" />
          <span className="mtc-linha" style={{ gap: "var(--e-3)" }}><Relogio /><AlternarTema /></span>
        </div>
        <div className="mtc-palco__centro">
          <p className="mtc-palco__nome">{marca.nome}</p>
          <div className="mtc-palco__rotulo">{rotulo}</div>
          {frase && <p className="mtc-palco__frase">{frase}</p>}
        </div>
        <div className="mtc-palco__rodape">Acesso protegido. Nunca pedimos sua senha por telefone ou WhatsApp.</div>
        <GraficoAmbiente semente={marca.nome} />
      </aside>
      <main className="mtc-acesso__lado">
        <div className="mtc-acesso__formulario">
          {voltarHref && <a className="mtc-voltar" href={voltarHref}><IconeVoltar tamanho={16} />Voltar</a>}
          {children}
        </div>
        {rodape && <div className="mtc-acesso__rodape">{rodape}</div>}
      </main>
    </div>
  );
}
